import { useState } from 'react';
import {
  useDetectionRules,
  createDetectionRule,
  updateDetectionRule,
  deleteDetectionRule,
  testDetectionRule,
} from '../../hooks/useSecOps';
import type { DetectionRule } from '../../hooks/useSecOps';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
  info: 'bg-gray-100 text-gray-600',
};

function Badge({ value, colors }: { value: string; colors?: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${(colors ?? {})[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

const EMPTY_FORM = {
  name: '',
  description: '',
  severity: 'medium',
  rule_type: 'scheduled',
  kql_query: '',
  lookback_duration: '1h',
  run_interval: '5m',
  mitre_techniques: '',
  tags: '',
  category: '',
};

export default function DetectionRulesPage() {
  const { data, loading, error, refresh } = useDetectionRules();
  const rules = (data?.data ?? []) as DetectionRule[];

  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; match_count: number; results: unknown[]; error?: string; columns?: string[]; execution_time_ms?: number } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setTestResult(null);
    setShowEditor(true);
  }

  function openEdit(rule: DetectionRule) {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      description: rule.description,
      severity: rule.severity,
      rule_type: rule.rule_type,
      kql_query: rule.kql_query,
      lookback_duration: rule.lookback_duration,
      run_interval: rule.run_interval,
      mitre_techniques: rule.mitre_techniques.join(', '),
      tags: rule.tags.join(', '),
      category: rule.category ?? '',
    });
    setTestResult(null);
    setShowEditor(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        mitre_techniques: form.mitre_techniques.split(',').map(s => s.trim()).filter(Boolean),
        mitre_tactics: [],
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await updateDetectionRule(editingId, payload);
      } else {
        await createDetectionRule(payload);
      }
      setShowEditor(false);
      refresh();
    } catch { /* handled */ } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testDetectionRule(form.kql_query, form.lookback_duration);
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, match_count: 0, results: [], error: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this detection rule?')) return;
    setDeleting(id);
    try {
      await deleteDetectionRule(id);
      refresh();
    } catch { /* handled */ } finally {
      setDeleting(null);
    }
  }

  async function handleToggle(rule: DetectionRule) {
    try {
      await updateDetectionRule(rule.id, { is_enabled: !rule.is_enabled });
      refresh();
    } catch { /* handled */ }
  }

  if (loading && rules.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Detection Rules</h1>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-gray-200 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Detection Rules</h1>
          <p className="text-sm text-gray-500 mt-1">Custom KQL detection rules running against Defend ADX</p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
          + New Rule
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>}

      {/* Rule Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Rule' : 'Create Detection Rule'}</h2>
              <button onClick={() => setShowEditor(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Suspicious PowerShell Execution" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Severity</label>
                    <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                      <option value="info">Info</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                    <select value={form.rule_type} onChange={e => setForm({ ...form, rule_type: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                      <option value="scheduled">Scheduled</option>
                      <option value="near_real_time">Near Real-Time</option>
                      <option value="anomaly">Anomaly</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Detects suspicious PowerShell commands..." />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">KQL Query</label>
                <textarea value={form.kql_query} onChange={e => setForm({ ...form, kql_query: e.target.value })}
                  rows={8}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono bg-gray-50"
                  placeholder={`ProcessEvents\n| where ProcessName endswith "powershell.exe"\n| where CommandLine has_any ("-enc", "-e ", "bypass", "hidden")\n| project Timestamp, DeviceName, UserName, CommandLine`}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Lookback</label>
                  <select value={form.lookback_duration} onChange={e => setForm({ ...form, lookback_duration: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="15m">15 minutes</option>
                    <option value="30m">30 minutes</option>
                    <option value="1h">1 hour</option>
                    <option value="4h">4 hours</option>
                    <option value="24h">24 hours</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Run Interval</label>
                  <select value={form.run_interval} onChange={e => setForm({ ...form, run_interval: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="5m">Every 5 min</option>
                    <option value="15m">Every 15 min</option>
                    <option value="30m">Every 30 min</option>
                    <option value="1h">Every hour</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="">—</option>
                    <option value="endpoint">Endpoint</option>
                    <option value="network">Network</option>
                    <option value="identity">Identity</option>
                    <option value="cloud">Cloud</option>
                    <option value="email">Email</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">MITRE Techniques (comma-separated)</label>
                  <input value={form.mitre_techniques} onChange={e => setForm({ ...form, mitre_techniques: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="T1059.001, T1027" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tags (comma-separated)</label>
                  <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="powershell, evasion" />
                </div>
              </div>

              {/* Test Results */}
              {testResult && (
                <div className={`rounded-lg p-4 text-sm ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-semibold ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {testResult.success ? `${testResult.match_count} match${testResult.match_count !== 1 ? 'es' : ''} found` : 'Query failed'}
                    </span>
                    {testResult.execution_time_ms != null && (
                      <span className="text-xs text-gray-400">{testResult.execution_time_ms}ms</span>
                    )}
                  </div>
                  {testResult.error && <p className="text-red-700">{testResult.error}</p>}
                  {testResult.success && testResult.results.length > 0 && (
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b">
                            {(testResult.columns ?? Object.keys(testResult.results[0] as Record<string, unknown>)).slice(0, 6).map(col => (
                              <th key={col} className="text-left py-1 pr-3 font-medium text-gray-600">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {testResult.results.slice(0, 5).map((row, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              {(testResult.columns ?? Object.keys(row as Record<string, unknown>)).slice(0, 6).map(col => (
                                <td key={col} className="py-1 pr-3 text-gray-700 truncate max-w-[200px]">
                                  {String((row as Record<string, unknown>)[col] ?? '')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 flex items-center justify-between">
              <button onClick={handleTest} disabled={testing || !form.kql_query.trim()}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium">
                {testing ? 'Testing...' : 'Test Query'}
              </button>
              <div className="flex gap-3">
                <button onClick={() => setShowEditor(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.kql_query.trim()}
                  className="px-6 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                  {saving ? 'Saving...' : editingId ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rule List */}
      {rules.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">No custom detection rules yet</p>
          <button onClick={openCreate} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Create your first rule</button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-left text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3 font-medium">Rule</th>
                <th className="px-4 py-3 font-medium">Severity</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Interval</th>
                <th className="px-4 py-3 font-medium">MITRE</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(rule => (
                <tr key={rule.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{rule.name}</p>
                    {rule.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[300px]">{rule.description}</p>}
                  </td>
                  <td className="px-4 py-3"><Badge value={rule.severity} colors={SEVERITY_COLORS} /></td>
                  <td className="px-4 py-3"><Badge value={rule.rule_type} /></td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{rule.run_interval}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {rule.mitre_techniques.slice(0, 3).map(t => (
                        <span key={t} className="text-[10px] font-mono bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">{t}</span>
                      ))}
                      {rule.mitre_techniques.length > 3 && (
                        <span className="text-[10px] text-gray-400">+{rule.mitre_techniques.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggle(rule)}
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${rule.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {rule.is_enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(rule)} className="text-xs text-indigo-600 hover:text-indigo-800 mr-3">Edit</button>
                    <button onClick={() => handleDelete(rule.id)} disabled={deleting === rule.id}
                      className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50">
                      {deleting === rule.id ? '...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
